import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle, Download, Loader2, MapPin } from "lucide-react";

interface ImportStats {
  total_fetched: number;
  inserted: number;
  skipped_existing: number;
  skipped_invalid: number;
}

interface ImportResult {
  success: boolean;
  stats?: ImportStats;
  message?: string;
  error?: string;
  details?: string;
}

interface ImportCemeteriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ImportCemeteriesDialog({ open, onOpenChange }: ImportCemeteriesDialogProps) {
  
  const queryClient = useQueryClient();
  const [result, setResult] = useState<ImportResult | null>(null);

  const importMutation = useMutation({
    mutationFn: async (): Promise<ImportResult> => {
      const { data, error } = await supabase.functions.invoke("import-cemeteries");

      if (error) {
        throw error;
      }

      return data as ImportResult;
    },
    onSuccess: (data) => {
      setResult(data);
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ["cemeteries"] });
        toast.success("Tuonti onnistui", { description: data.message });
      } else {
        toast.error("Tuonti epäonnistui", { description: data.error });
      }
    },
    onError: (error) => {
      console.error("Import error:", error);
      setResult({
        success: false,
        error: "Tuonti epäonnistui",
        details: error instanceof Error ? error.message : "Tuntematon virhe",
      });
      toast.error("Tuonti epäonnistui", { description: "Yritä myöhemmin uudelleen" });
    },
  });

  const handleClose = () => {
    if (!importMutation.isPending) {
      setResult(null);
      onOpenChange(false);
    }
  };

  const handleImport = () => {
    setResult(null);
    importMutation.mutate();
  };

  const isLoading = importMutation.isPending;
  const isComplete = result !== null;
  const isSuccess = result?.success === true;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isComplete && isSuccess ? (
              <>
                <CheckCircle className="h-5 w-5 text-green-500" />
                Tuonti valmis!
              </>
            ) : isComplete && !isSuccess ? (
              <>
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Tuonti epäonnistui
              </>
            ) : (
              <>
                <MapPin className="h-5 w-5" />
                Tuo hautausmaat OpenStreetMapista
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isComplete && isSuccess
              ? "Hautausmaat on tuotu onnistuneesti tietokantaan."
              : isComplete && !isSuccess
              ? result?.details || "Jokin meni pieleen tuonnin aikana."
              : "Tuo Suomen hautausmaat OpenStreetMapista (OSM)."}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {isLoading ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <div className="text-center">
                <p className="font-medium">Tuodaan hautausmaita...</p>
                <p className="text-sm text-muted-foreground">
                  Tämä voi kestää 30-60 sekuntia
                </p>
              </div>
            </div>
          ) : isComplete && isSuccess && result.stats ? (
            <div className="space-y-4">
              <div className="rounded-lg bg-muted p-4 space-y-2">
                <h4 className="font-medium">Tulokset:</h4>
                <ul className="space-y-1 text-sm">
                  <li className="flex justify-between">
                    <span>Uusia hautausmaita:</span>
                    <span className="font-semibold text-green-600">
                      {result.stats.inserted}
                    </span>
                  </li>
                  <li className="flex justify-between">
                    <span>Ohitettuja (jo olemassa):</span>
                    <span className="text-muted-foreground">
                      {result.stats.skipped_existing}
                    </span>
                  </li>
                  <li className="flex justify-between">
                    <span>Virheellisiä (ei nimeä):</span>
                    <span className="text-muted-foreground">
                      {result.stats.skipped_invalid}
                    </span>
                  </li>
                </ul>
              </div>
              <p className="text-xs text-muted-foreground">
                Lähde: OpenStreetMap (ODbL-lisenssi)
              </p>
            </div>
          ) : isComplete && !isSuccess ? (
            <div className="rounded-lg bg-destructive/10 p-4">
              <p className="text-sm text-destructive">
                {result?.error || "Tuntematon virhe"}
              </p>
              {result?.details && (
                <p className="text-xs text-muted-foreground mt-2">
                  {result.details}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Arviolta 500-800 hautausmaata Suomesta</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Koordinaatit, nimet ja kaupungit</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Olemassa olevia EI korvata</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-muted-foreground">•</span>
                  <span className="text-muted-foreground">
                    Osoitteet usein puutteelliset OSM:ssä
                  </span>
                </li>
              </ul>
              <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/30 p-3 rounded-lg">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>Tuonti kestää 30-60 sekuntia</span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {isComplete ? (
            <Button onClick={handleClose}>Sulje</Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isLoading}>
                Peruuta
              </Button>
              <Button onClick={handleImport} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Tuodaan...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Tuo hautausmaat
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
